import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { IconUser } from '../components/icons';
import { getApiBaseUrl } from '../services/apiClient';
import { useHealthData } from '../services/HealthDataContext';
import { formatTime } from '../services/labels';
import { useDataSourceMode, type DataSourceMode } from '../services/useDataSourceMode';
import { useThemeMode, type ThemeMode } from '../theme/useThemeMode';

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'system', label: 'Sistema' },
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Oscuro' },
];

const SOURCE_OPTIONS: Array<{ value: DataSourceMode; label: string }> = [
  { value: 'demo', label: 'Demo local' },
  { value: 'cloudflare', label: 'Cloudflare (D1)' },
];

export function ProfileScreen() {
  const [themeMode, setThemeMode] = useThemeMode();
  const [sourceMode, setSourceMode] = useDataSourceMode();
  const { mode, backendError, lastSyncAt, resyncDemoData } = useHealthData();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleResync = async () => {
    setIsSyncing(true);
    try {
      await resyncDemoData();
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="screen screen--profile">
      <div className="profile-header">
        <div className="profile-header__avatar">
          <IconUser size={28} />
        </div>
        <div>
          <p className="profile-header__name">Ivan</p>
          <span className="profile-header__badge">
            {mode === 'cloudflare' ? 'Conectado a Cloudflare (D1)' : 'Modo demo · 90 dias simulados'}
          </span>
        </div>
      </div>

      <Card title="Apariencia">
        <div className="segmented-control">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`segmented-control__option${themeMode === option.value ? ' segmented-control__option--active' : ''}`}
              onClick={() => setThemeMode(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Backend" subtitle={getApiBaseUrl()}>
        <div className="segmented-control">
          {SOURCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`segmented-control__option${sourceMode === option.value ? ' segmented-control__option--active' : ''}`}
              onClick={() => setSourceMode(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {backendError ? <p className="debug-error">{backendError}</p> : null}

        {mode === 'cloudflare' ? (
          <>
            <p className="debug-row">
              Ultima sincronizacion: <strong>{lastSyncAt ? formatTime(lastSyncAt) : 'Nunca'}</strong>
            </p>
            <button type="button" className="primary-button" onClick={handleResync} disabled={isSyncing}>
              {isSyncing ? 'Sincronizando…' : 'Sincronizar datos demo ahora'}
            </button>
          </>
        ) : (
          <p className="hint-text">
            Elegi "Cloudflare (D1)" para que la app lea y escriba en el backend real (`apps/api`, `npm run dev` en ese
            paquete) en lugar de generar datos demo en el propio navegador.
          </p>
        )}
      </Card>

      <Card title="Fuentes de datos">
        <ul className="source-list">
          <li className="source-list__item">
            <span>Apple Health (HealthKit)</span>
            <span className="source-list__status">No conectado</span>
          </li>
          <li className="source-list__item">
            <span>Huawei Health</span>
            <span className="source-list__status">Via Apple Health</span>
          </li>
        </ul>
        <p className="hint-text">
          La conexion con HealthKit se prueba desde <Link to="/health-debug">/health-debug</Link> (pantalla temporal
          del Sprint 2).
        </p>
      </Card>

      <Card title="Datos y privacidad">
        <ul className="action-list">
          <li>
            <button type="button" disabled>
              Exportar mis datos
            </button>
          </li>
          <li>
            <button type="button" disabled>
              Eliminar mis datos
            </button>
          </li>
          <li>
            <button type="button" disabled>
              Eliminar mi cuenta
            </button>
          </li>
          <li>
            <button type="button" disabled>
              Desconectar Apple Health
            </button>
          </li>
        </ul>
        <p className="hint-text">El backend de Cloudflare ya existe (Sprint 3), pero estas acciones todavia no tienen endpoint.</p>
      </Card>

      <p className="disclaimer">VitalAI no realiza diagnosticos medicos. Consulta a un profesional de salud ante cualquier duda.</p>
    </div>
  );
}
